import { useMemo, useState } from "react";
import { Button, SimpleTable, Badge, CollapsibleBar } from "@components";
import { formatAmount } from "@utils";
import { invoiceLabels } from "@components/paymentTemplates/TemplateTile";
import { statusLabel } from "../../PaymentRequestDrawers/utils";
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { Download } from "react-bootstrap-icons";
import downloadCsv from "./downloadCsv";

const isErrorStatus = (status) =>
  ["amountTooSmall", "noContactDetails", "noPhone", "noEmail"].includes(status);

const statusColors = {
  amountTooSmall: "red",
  noContactDetails: "red",
  noPhone: "red",
  noEmail: "red",
};

export default function InvalidAccountsReviewStep({
  rows,
  smsSelected,
  emailSelected,
  sendableCount,
  sendableAmount,
  invoicesRequested,
  customAmount,
  onBack,
  onProceed,
  isSingleCustomer,
}) {
  const [openPanel, setOpenPanel] = useState(null);

  const bothChannels = smsSelected && emailSelected;
  const hideAmounts = invoicesRequested === "None";

  const criticalRows = useMemo(() => {
    if (!rows?.length) return [];
    if (isSingleCustomer)
      return rows.filter((r) => r.status === "amountTooSmall");
    return rows.filter((r) => isErrorStatus(r.status));
  }, [rows, isSingleCustomer]);

  const partialRows = useMemo(() => {
    if (isSingleCustomer) return [];
    if (!bothChannels || !rows?.length) return [];
    return rows.filter(
      (r) => r.status === "ready" && r.phoneError !== r.emailError,
    );
  }, [rows, bothChannels, isSingleCustomer]);

  const criticalAmount = useMemo(
    () => criticalRows.reduce((sum, r) => sum + (r.minimumAmount || 0), 0),
    [criticalRows],
  );

  const handleExport = () => {
    const criticalColumns = [
      { label: "Account", value: (r) => r.customerName },
      { label: "Account Code", value: (r) => r.reference },
      ...(emailSelected
        ? [{ label: "Email", value: (r) => r.email || "No email" }]
        : []),
      ...(smsSelected
        ? [{ label: "Phone", value: (r) => r.phone || "No phone" }]
        : []),
      { label: "Amount", value: (r) => r.minimumAmount },
      { label: "Reason", value: (r) => statusLabel(r.status) },
    ];

    const partialColumns = [
      { label: "Account", value: (r) => r.customerName },
      { label: "Account Code", value: (r) => r.reference },
      { label: "Phone", value: (r) => r.phone || "No phone" },
      { label: "Email", value: (r) => r.email || "No email" },
      { label: "Amount", value: (r) => r.minimumAmount },
    ];

    downloadCsv("invalid_accounts", [
      {
        title: "Excluded Accounts",
        columns: criticalColumns,
        rows: criticalRows,
      },
      {
        title: "Partial Accounts (single channel only)",
        columns: partialColumns,
        rows: partialRows,
      },
    ]);
  };

  const phoneLabel = (r, errorClass) => {
    if (!r.phoneError) return <span title={r.phone}>{r.phone}</span>;
    const text = r.phone ? `Invalid mobile · ${r.phone}` : "No phone";
    return (
      <span className={errorClass} title={text}>
        {text}
      </span>
    );
  };

  const emailLabel = (r, errorClass) => {
    if (!r.emailError) return <span title={r.email}>{r.email}</span>;
    const text = r.email ? `Invalid email · ${r.email}` : "No email";
    return (
      <span className={errorClass} title={text}>
        {text}
      </span>
    );
  };

  const criticalTableConfig = useMemo(
    () => ({
      columns: [
        {
          title: "Account",
          width: "200px",
          render: (r) => <span title={r.customerName}>{r.customerName}</span>,
        },
        {
          title: "Email",
          render: (r) => emailLabel(r, "text-red-600 font-semibold"),
          shouldShow: () => emailSelected,
        },
        {
          title: "Phone",
          width: "150px",
          render: (r) => phoneLabel(r, "text-red-600 font-semibold"),
          shouldShow: () => smsSelected,
        },
        {
          title: "Amount",
          width: "120px",
          alignment: "right",
          render: (r) =>
            r.amountError ? (
              <span
                className="text-red-600 font-semibold"
                title="Balance < £0.30"
              >
                Balance &lt; £0.30
              </span>
            ) : (
              <span title={formatAmount(r.minimumAmount)}>
                {formatAmount(r.minimumAmount)}
              </span>
            ),
        },
      ],
    }),
    [smsSelected, emailSelected],
  );

  const partialTableConfig = useMemo(
    () => ({
      columns: [
        {
          title: "Account",
          width: "200px",
          render: (r) => <span title={r.customerName}>{r.customerName}</span>,
        },
        {
          title: "Phone",
          width: "150px",
          render: (r) => phoneLabel(r, "text-zinc-400"),
        },
        {
          title: "Email",
          render: (r) => emailLabel(r, "text-zinc-400"),
        },
        {
          title: "Amount",
          width: "120px",
          alignment: "right",
          render: (r) => (
            <span title={formatAmount(r.minimumAmount)}>
              {formatAmount(r.minimumAmount)}
            </span>
          ),
        },
      ],
    }),
    [],
  );

  const toggle = (panel) =>
    setOpenPanel((prev) => (prev === panel ? null : panel));

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col p-4 gap-3">
        {criticalRows.length > 0 && (
          <CollapsibleBar
            icon={
              <ExclamationTriangleIcon className="size-4 shrink-0 text-red-600" />
            }
            borderColor="border-red-200"
            bgColor="bg-red-50"
            textColor="text-zinc-700"
            accentColor="text-red-600"
            open={openPanel === "critical"}
            onToggle={() => toggle("critical")}
            summary={
              <>
                <strong>{criticalRows.length} </strong>{" "}
                {criticalRows.length === 1 ? "account" : "accounts"} can&apos;t
                be sent a request
                {!hideAmounts && criticalAmount > 0 && (
                  <> &nbsp;({formatAmount(criticalAmount)})</>
                )}
              </>
            }
            count={criticalRows.length}
          >
            <SimpleTable
              data={criticalRows}
              config={criticalTableConfig}
              noFooter
              rowHeight="sm"
              compact
              embedded
              lazyLoad
            />
          </CollapsibleBar>
        )}

        {partialRows.length > 0 && (
          <CollapsibleBar
            icon={
              <InformationCircleIcon className="size-4 shrink-0 text-zinc-400" />
            }
            borderColor="border-zinc-200"
            bgColor="bg-zinc-50"
            textColor="text-zinc-700"
            accentColor="text-zinc-500"
            open={openPanel === "partial"}
            onToggle={() => toggle("partial")}
            summary={
              <>
                <strong>{partialRows.length} </strong>{" "}
                {partialRows.length === 1 ? "account" : "accounts"} will be
                requested via Email / SMS only
              </>
            }
            count={partialRows.length}
          >
            <SimpleTable
              data={partialRows}
              config={partialTableConfig}
              noFooter
              rowHeight="sm"
              compact
              embedded
              lazyLoad
            />
          </CollapsibleBar>
        )}
      </div>

      <div className="shrink-0 border-t border-zinc-200">
        {!hideAmounts && invoicesRequested !== "Custom" && (
          <div className="px-4 pt-3 text-sm flex justify-end items-end text-zinc-600">
            <span className="mr-2">
              {`${invoiceLabels[invoicesRequested]} (${sendableCount} ${sendableCount === 1 ? "account" : "accounts"})`}
              &nbsp;&nbsp;&nbsp;
              <span className="font-semibold text-zinc-900 text-2xl">
                {formatAmount(sendableAmount)}
              </span>
            </span>
          </div>
        )}
        {invoicesRequested === "Custom" && (
          <div className="px-4 pt-3 text-sm flex justify-end items-end text-zinc-600">
            <span className="mr-2">
              {`Custom amount (${sendableCount} ${sendableCount === 1 ? "account" : "accounts"})`}
              &nbsp;&nbsp;&nbsp;
              <span className="font-semibold text-zinc-900 text-2xl">
                {formatAmount(customAmount || 0)}
              </span>
            </span>
          </div>
        )}
        <div className="p-4 flex items-center justify-between">
          <Button onClick={onBack} color="secondary">
            Back to Preview
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} color="secondary">
              <Download className="mr-1.5" />
              Export
            </Button>
            {isSingleCustomer ? (
              <Button disabled color="primary">
                Can&apos;t send — balance too small
              </Button>
            ) : (
              <Button
                onClick={onProceed}
                color="primary"
                disabled={sendableCount === 0}
              >
                Confirm and continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

